import {
  add,
  addDays,
  areIntervalsOverlapping,
  isSameDay,
  isEqual,
  set,
  isAfter,
  isBefore,
  getHours,
  getMinutes,
  isPast,
  isWithinInterval,
  isDate,
  startOfDay,
  endOfDay,
} from "date-fns";
import defaultTo from "lodash/defaultTo";
import filter from "lodash/filter";
import find from "lodash/find";
import findLast from "lodash/findLast";
import flatMap from "lodash/flatMap";
import forEach from "lodash/forEach";
import get from "lodash/get";
import has from "lodash/has";
import isArray from "lodash/isArray";
import isInteger from "lodash/isInteger";
import map from "lodash/map";
import min from "lodash/min";
import uniqBy from "lodash/uniqBy";
import unionBy from "lodash/unionBy";
import sessions from "./sessions";
import { CARE_TYPE } from "./activity-functions";
import {
  SHIFTCOVER_STATUS_OPEN,
  SHIFTCOVER_STATUS_VOLUNTEERED,
} from "../constants/shift-cover-requests";

const MIN_SHIFT_COVER_GAP_SECONDS = 60 * 60; // 60 minutes in seconds

/**
 * Does the shift start within the session start/end times?
 * @param {Object} shift
 * @param {Object} session
 * @return bool
 */
function shiftStartsInSession(shift, session) {
  const sessionStart = set(shift.startDate, session.start);
  const sessionEnd = set(add(shift.startDate, session.endAdjust), session.end);
  return (
    isEqual(shift.startDate, sessionStart) ||
    (isAfter(shift.startDate, sessionStart) &&
      isBefore(shift.startDate, sessionEnd))
  );
}

/**
 * Given an array of shifts return only those shifts that are
 * assigned or have been assigned previously to the given userId
 * @param {Array} shifts
 * @param {string} userId
 * @returns {Array}
 */
function filterShiftsForUser(shifts, userId) {
  return shifts.filter(
    (shift) => find(get(shift, "assignedTo", []), { userId }) !== undefined,
  );
}

/**
 * Given an array of shifts return only those shifts that are
 * assigned or have been assigned previously to the given userId
 * @param {Array} shifts
 * @param {string} userId
 * @returns {Array}
 */
function filterCurrentShiftsForUser(shifts, userId) {
  return shifts.filter(
    (shift) =>
      find(get(shift, "assignedTo", []), { type: "current", userId }) !==
      undefined,
  );
}

function filterShiftsNotCurrentlyAssigned(shifts) {
  return shifts.filter((shift) => {
    if (shift.assignedTo === undefined || shift.assignedTo.length == 0)
      return true;
    const currentAssignment = find(shift.assignedTo, { type: "current" });
    return currentAssignment === undefined;
  });
}

/**
 * Find all shifts that have either a cover request or are not currently assigned. Used to build 'take on this shift' UI.
 * @param {Array} shifts
 * @returns {Array}
 */
function filterShiftsWithCoverRequestsOrNotCurrentlyAssigned(shifts) {
  return shifts.filter((shift) => {
    if (shift.coverRequestStatus !== undefined) {
      return true;
    }
    if (shift.assignedTo === undefined || shift.assignedTo.length == 0) {
      if (shift.coverNotRequired === true) {
        return false;
      }
      return true;
    }
    const currentAssignment = find(shift.assignedTo, { type: "current" });
    if (currentAssignment === undefined) {
      if (shift.coverNotRequired === true) {
        return false;
      }
      return true;
    }
    return false;
  });
}

/**
 * Given an array of shifts return only those shifts that are
 * assigned currently to the given agencyId
 * @param {Array} shifts
 * @param {string} agencyId
 * @returns {Array}
 */
function filterCurrentShiftsForAgencyId(shifts, agencyId) {
  return shifts.filter((shift) => {
    if (shift.assignedTo === undefined || shift.assignedTo.length == 0)
      return false;
    const currentAssignment = find(shift.assignedTo, { type: "current" });
    return currentAssignment?.agencyId === agencyId;
  });
}

/**
 * Given an array of shifts return only those shifts that start
 * on the given day
 * @param {Array} shifts
 * @param {Date} rotaStartDate
 * @param {int} dayIndex Integer representing the day number since the rotaStartDate
 * @returns {Array}
 */
function filterShiftsForDayIndex(shifts, rotaStartDate, dayIndex = 0) {
  const theDay = addDays(rotaStartDate, dayIndex);
  return shifts.filter((shift) => isSameDay(shift.startDate, theDay));
}

/**
 * Given an array of shifts return only those shifts that occur
 * between the given start and end days
 * @param {Array} shifts
 * @param {Date} rotaStartDate
 * @param {int} startDayIndex Integer representing the day number since the rotaStartDate
 * @param {int} endDayIndex Integer representing the day number since the rotaStartDate
 * @returns {Array}
 */
function filterShiftsForDayIndexRange(
  shifts,
  rotaStartDate,
  startDayIndex,
  endDayIndex,
) {
  const startDay = startOfDay(addDays(rotaStartDate, startDayIndex));
  const endDay = endOfDay(addDays(rotaStartDate, endDayIndex));
  return shifts.filter((shift) =>
    isWithinInterval(shift.startDate, {
      start: startDay,
      end: endDay,
    }),
  );
}

/**
 * Returns the dayIndex based on the given shift and the rota.
 *
 * @param {Object} shift
 * @param {Object} rota
 * @return {int} dayIndex
 */
function getDayIndexForShiftRelativeToRota(shift, rota) {
  for (let i = 0; i < 14; i += 1) {
    const rotaDay = addDays(rota.startDate, i);
    if (isSameDay(shift.startDate, rotaDay)) {
      return i;
    }
  }
  return undefined;
}

/**
 * Returns an object keyed by userId/agencyId
 *
 * UUIDv4: {
 *    userId: UUIDv4, // if user
 *    agencyId: UUIDv4, // if agency
 *    type: 'user', // ['user', 'agency', 'nobody']
 *    shifts: [...],
 * },
 * ...
 *
 * If the shift hasn't been assigned yet, or has no current assignment
 * then it is returned with UUIDv4 set to 'nobody' and 'type' set to `nobody`.
 * If the shift is assigned to an agency user, thee type is set to 'agency', and
 * instead of userId an agencyId is returned.
 *
 * Shifts can be optionally filtered to a particular start/end date,
 * which is useful when rendering a single week of the rota.
 *
 * @param {Array} shifts
 * @param {Object} session
 * @param {Date} rotaStartDate
 * @param {int} startDayIndex Integer representing the day number since the rotaStartDate
 * @param {int} endDayIndex Integer representing the day number since the rotaStartDate
 * @param {array} allowedAssignmentTypes Array of the types of assignments you want returned
 *                                       (ignores old assignments by default)
 * @returns {Object}
 */
function getShiftsForSessionGroupedByAssignedTo(
  shifts,
  session,
  rotaStartDate = undefined,
  startDayIndex = undefined,
  endDayIndex = undefined,
  allowedAssignmentTypes = ["current", "sick", "holiday"],
) {
  if (!Array.isArray(shifts)) {
    return {};
  }

  let filteredShifts = shifts;

  // we might want to filter the shifts to a particular date range
  if (
    isDate(rotaStartDate) &&
    isInteger(startDayIndex) &&
    isInteger(endDayIndex)
  ) {
    filteredShifts = filterShiftsForDayIndexRange(
      filteredShifts,
      rotaStartDate,
      startDayIndex,
      endDayIndex,
    );
  }

  // we're only interested in the shifts for the given session
  filteredShifts = filteredShifts.filter((shift) =>
    shiftStartsInSession(shift, session),
  );

  if (filteredShifts.length === 0) {
    return {};
  }

  const shiftsByUserId = {};
  const nobodyResults = [];

  filteredShifts.forEach((shift) => {
    if (!has(shift, "assignedTo")) {
      // shift has never been assigned
      nobodyResults.push(shift);
    } else if (find(shift.assignedTo, { type: "current" }) === undefined) {
      // shift has no "current" assignment
      nobodyResults.push(shift);
    }

    // iterate over assignedTo and put in the appropriate userId buckets
    forEach(shift.assignedTo, (assignment) => {
      if (!allowedAssignmentTypes.includes(assignment.type)) {
        // ignore assignments that are not of the correct type
        return;
      }
      if (has(assignment, "userId")) {
        if (!has(shiftsByUserId, assignment.userId)) {
          // this user isn't already in the results object, so add it
          shiftsByUserId[assignment.userId] = {
            type: "user",
            userId: assignment.userId,
            shifts: [shift],
          };
        } else if (!shiftsByUserId[assignment.userId].shifts.includes(shift)) {
          // this user is already in the results object, add another shift to the user
          shiftsByUserId[assignment.userId].shifts.push(shift);
        }
        // store the latest display name for a user - used to assist sorting
        shiftsByUserId[assignment.userId].displayName = assignment.displayName;
      } else if (has(assignment, "agencyId")) {
        if (!has(shiftsByUserId, assignment.agencyId)) {
          // this agency isn't already in the results object, so add it
          shiftsByUserId[assignment.agencyId] = {
            type: "agency",
            agencyId: assignment.agencyId,
            shifts: [shift],
          };
        } else if (
          !shiftsByUserId[assignment.agencyId].shifts.includes(shift)
        ) {
          // this agency is already in the results object, add another shift to the agency
          shiftsByUserId[assignment.agencyId].shifts.push(shift);
        }
      } /* v8 ignore next 3 */ else {
        // fail-safe - this shouldn't happen. it would indicate bad data
        throw new Error("Cannot have assignment without agencyId or userId");
      }
    });
  });

  // push nobody results to the object as the last object key
  if (nobodyResults.length > 0) {
    // order nobody results by dutiesRequired, so HOC / Senior show first before regular shifts
    nobodyResults.sort(
      (a, b) =>
        (b.dutiesRequired ? b.dutiesRequired.length : 0) -
        (a.dutiesRequired ? a.dutiesRequired.length : 0),
    );
    shiftsByUserId.nobody = { type: "nobody", shifts: nobodyResults };
  }

  return shiftsByUserId;
}

/**
 * Given the session and a dayIndex calculate how many shifts occur on that day
 * and how many have a `current` assignment.
 * @param {Object} rota
 * @param {Object} session
 * @param {int} dayIndex Integer representing the day number since the rota.startDate
 * @returns {Array} [int shiftsCurrentlyAssigned, int shiftsTotalForDay]
 */
function getShiftCountsForSessionAndDayIndex(rota, session, dayIndex) {
  if (!has(rota, "shifts")) {
    return [0, 0];
  }

  const shifts = rota.shifts.filter((shift) =>
    shiftStartsInSession(shift, session),
  );
  if (Object.keys(shifts).length === 0) {
    return [0, 0];
  }

  const theDay = addDays(rota.startDate, dayIndex);
  const shiftsForDay = shifts.filter((shift) =>
    isSameDay(shift.startDate, theDay),
  );

  const shiftsCurrentlyAssigned = filter(shiftsForDay, (shift) =>
    find(get(shift, "assignedTo", []), { type: "current" }),
  );

  return [shiftsCurrentlyAssigned.length, shiftsForDay.length];
}

/**
 * Given a shift object, return the session object that matches the shift's times.
 * return null on no match (i.e. custom times)
 *
 * @param {Object} shift
 * @returns {Object|null}
 */
function getShiftSession(shift) {
  const session = find(sessions, {
    staffStart: {
      hours: getHours(shift.startDate),
      minutes: getMinutes(shift.startDate),
    },
    staffEnd: {
      hours: getHours(shift.endDate),
      minutes: getMinutes(shift.endDate),
    },
  });
  return session === undefined ? null : session;
}

/**
 * Given a userId and an array of assignments,
 * return the most recent assignment for that user
 * @param {String} userId
 * @param {Array} assignments
 * @returns {Object|undefined}
 */
function getMostRecentAssignmentFromUserId(userId, assignments) {
  return findLast(assignments, { userId });
}

/**
 * Are the start and end shift times "custom"?
 * e.g. do they match the standard sessions start/end times
 * @param {Object} shift
 * @returns {bool}
 */
function shiftHasCustomTimes(shift) {
  return getShiftSession(shift) == null;
}

/**
 * Determine if a shift is assigned to an agency worker or not
 * @param {Object} shift
 * @returns bool
 */
function shiftIsAssignedToAgency(shift) {
  const current = find(shift.assignedTo, { type: "current" });
  if (current === undefined) {
    return false;
  }
  return has(current, "agencyId");
}

/**
 * Given an array of activities, return those that are linked to this shift
 * Now sorted by startDate.
 *
 * @param {Array} activities
 * @param {Object} shift
 * @returns {Array}
 */
function getActivitiesForShift(activities, shift) {
  if (activities === undefined) {
    return [];
  }
  const result = filter(activities, (activity) =>
    map(shift.activities, "activityId").includes(activity.id),
  );
  return result.sort((a, b) => a.startDate - b.startDate);
}

/**
 * Given an array of activities, return those that are during this shift's start and end times,
 * sorted by time. This is used so people can select unlinked activities to link them to this shift.
 *
 * if includeAssignedToShift is specified,
 * also return any acitvities that have the shift linked to them.
 *
 * @param {Array} activities
 * @param {Object} shift
 * @param {options} { includeAssignedToShift: false }
 * @returns {Array}
 */
function getActivitiesWithinShiftTimes(activities, shift, options) {
  if (activities === undefined) {
    return [];
  }
  const opts = { includeAssignedToShift: false, ...options };
  let result = filter(activities, (activity) =>
    isWithinInterval(activity.startDate, {
      start: shift.startDate,
      end: shift.endDate,
    }),
  );

  if (opts.includeAssignedToShift) {
    const assignedToShift = filter(
      activities,
      (activity) =>
        find(get(activity, "shifts", []), { shiftId: shift.id }) !== undefined,
    );
    result = unionBy(result, assignedToShift, "id");
  }
  return result.sort((a, b) => a.startDate - b.startDate);
}

/**
 * Given an array of activities, return those that are during this shift's day,
 * sorted by time. This is used so people can select unlinked activities to link them to this shift.
 *
 * @param {Array} activities
 * @param {Object} shift
 * @returns {Array}
 */
function getActivitiesWithinShiftDay(activities, shift) {
  if (activities === undefined) {
    return [];
  }
  const result = filter(activities, (activity) =>
    isWithinInterval(activity.startDate, {
      start: startOfDay(shift.startDate),
      end: endOfDay(shift.endDate),
    }),
  );
  return result.sort((a, b) => a.startDate - b.startDate);
}

function sortShiftGroupings(data) {
  let sortedKeys = [];
  sortedKeys = Object.keys(data).sort((a, b) => {
    // move agencies to back
    if (data[a].userId && data[b].agencyId) {
      return -1;
    }
    if (data[a].agencyId && data[b].userId) {
      return 1;
    }
    // move nobody to back
    if (data[a].type !== "nobody" && data[b].type === "nobody") {
      return -1;
    }
    if (data[a].type === "nobody" && data[b].type !== "nobody") {
      return 1;
    }

    // collate all shift.dutiesRequired and sort by them
    // ONLY SHIFTS THAT ARE ASSIGNED TO THE CURRENT USER

    let currentShiftsB = [];
    if (data[b].userId) {
      currentShiftsB = filter(data[b].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          userId: data[b].userId,
          type: "current",
        }),
      );
    } else {
      currentShiftsB = filter(data[b].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          agencyId: data[b].agencyId,
          type: "current",
        }),
      );
    }
    let currentShiftsA = [];
    if (data[a].userId) {
      currentShiftsA = filter(data[a].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          userId: data[a].userId,
          type: "current",
        }),
      );
    } else {
      currentShiftsA = filter(data[a].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          agencyId: data[a].agencyId,
          type: "current",
        }),
      );
    }

    const dutiesB = defaultTo(flatMap(currentShiftsB, "dutiesRequired"), []);
    const dutiesA = defaultTo(flatMap(currentShiftsA, "dutiesRequired"), []);

    if (dutiesA.includes("hoc") && !dutiesB.includes("hoc")) {
      return -1;
    }
    if (dutiesB.includes("hoc") && !dutiesA.includes("hoc")) {
      return 1;
    }
    if (dutiesA.length > 0 || dutiesB.length > 0) {
      // if you have other duties, show first, depending on how many duties you're doing
      if (dutiesA.length > dutiesB.length) {
        return -1;
      }
      if (dutiesA.length < dutiesB.length) {
        return 1;
      }
    }

    // sort by displayName
    if (data[a].displayName < data[b].displayName) {
      return -1;
    }
    if (data[a].displayName > data[b].displayName) {
      return 1;
    }
    return 0;
  });
  return sortedKeys;
}

function sortShiftGroupingsByShiftTime(data) {
  let sortedKeys = [];
  sortedKeys = Object.keys(data).sort((a, b) => {
    // move agencies to back
    if (data[a].userId && data[b].agencyId) {
      return -1;
    }
    if (data[a].agencyId && data[b].userId) {
      return 1;
    }
    // move nobody to back
    if (data[a].type !== "nobody" && data[b].type === "nobody") {
      return -1;
    }
    if (data[a].type === "nobody" && data[b].type !== "nobody") {
      return 1;
    }

    // collate all shift.dutiesRequired and sort by them
    // ONLY SHIFTS THAT ARE ASSIGNED TO THE CURRENT USER

    let currentShiftsB = [];
    if (data[b].userId) {
      currentShiftsB = filter(data[b].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          userId: data[b].userId,
          type: "current",
        }),
      );
    } else {
      currentShiftsB = filter(data[b].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          agencyId: data[b].agencyId,
          type: "current",
        }),
      );
    }
    let currentShiftsA = [];
    if (data[a].userId) {
      currentShiftsA = filter(data[a].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          userId: data[a].userId,
          type: "current",
        }),
      );
    } else {
      currentShiftsA = filter(data[a].shifts, (shift) =>
        find(get(shift, "assignedTo", []), {
          agencyId: data[a].agencyId,
          type: "current",
        }),
      );
    }

    const dutiesB = defaultTo(flatMap(currentShiftsB, "dutiesRequired"), []);
    const dutiesA = defaultTo(flatMap(currentShiftsA, "dutiesRequired"), []);

    if (dutiesA.includes("hoc") && !dutiesB.includes("hoc")) {
      return -1;
    }
    if (dutiesB.includes("hoc") && !dutiesA.includes("hoc")) {
      return 1;
    }

    // take the earliest shift time
    const shiftTimesA = defaultTo(
      map(flatMap(currentShiftsA, "startDate"), (date) =>
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      ),
      [],
    );
    const shiftTimesB = defaultTo(
      map(flatMap(currentShiftsB, "startDate"), (date) =>
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      ),
      [],
    );
    if (min(shiftTimesA) < min(shiftTimesB)) {
      return -1;
    }
    if (min(shiftTimesA) > min(shiftTimesB)) {
      return 1;
    }

    // sort by displayName
    if (data[a].displayName < data[b].displayName) {
      return -1;
    }
    if (data[a].displayName > data[b].displayName) {
      return 1;
    }
    return 0;
  });
  return sortedKeys;
}

/**
 * @see getShiftsForSessionGroupedByAssignedTo()
 *
 * Returns the same data, but put into a sorted array, with the original key set as an 'id' property
 * e.g.
 * {
 *    userId1 : { type: 'user', userId: userId,  'shifts' : [shift1, shift2] },
 *    userId2 : { type: 'user', userId: userId2, 'shifts' : [shift1, shift2] },
 *    ...
 * }
 * becomes
 * [
 *   { id: userId,  sessionGrouping: { type: 'user', userId: userId,  'shifts' : [shift1, shift2] } },
 *   { id: userId2, sessionGrouping: { type: 'user', userId: userId2, 'shifts' : [shift1, shift2] } },
 *   ...
 * ]
 *
 * @param {Array} shifts
 * @param {Object} session
 * @param {Date} rotaStartDate
 * @param {int} startDayIndex Integer representing the day number since the rotaStartDate
 * @param {int} endDayIndex Integer representing the day number since the rotaStartDate
 * @returns {Array}
 */

function getShiftsForSessionGroupedByAssignedToSorted(
  shifts,
  session,
  rotaStartDate = undefined,
  startDayIndex = undefined,
  endDayIndex = undefined,
  sortOptions = undefined,
) {
  const sort = {
    sortBy: "name",
    allowedAssignmentTypes: ["current", "sick", "holiday"],
    ...sortOptions,
  };

  const shiftsByUserId = getShiftsForSessionGroupedByAssignedTo(
    shifts,
    session,
    rotaStartDate,
    startDayIndex,
    endDayIndex,
    sort.allowedAssignmentTypes,
  );
  let sortedKeys = [];
  switch (sort.sortBy) {
    /* v8 ignore next 3 */
    case "time":
      sortedKeys = sortShiftGroupingsByShiftTime(shiftsByUserId);
      break;
    default:
      sortedKeys = sortShiftGroupings(shiftsByUserId);
      break;
  }
  const sortedShifts = [];
  sortedKeys.forEach((key) => {
    sortedShifts.push({
      id: key,
      sessionGrouping: shiftsByUserId[key],
    });
  });
  return sortedShifts;
}

/**
 * Find all the shifts that have activities that the client IDs given are on
 *
 * @param {Array} shifts
 * @param {Array} clientIds
 * @param {Array} activities
 * @returns Array
 */
function getShiftsFilteredByClients(shifts, clientIds, activities) {
  const filteredShifts = shifts.filter((shift) => {
    const activitiesOnShift = getActivitiesForShift(activities, shift);
    const shiftHasClient = activitiesOnShift.some((activity) => {
      if (has(activity, "clients") && isArray(activity.clients)) {
        return activity.clients.some((client) =>
          clientIds.includes(client.clientId),
        );
      }
      return false;
    });
    return shiftHasClient;
  });
  return filteredShifts;
}

function filterGroupedShifts(shiftsForSession, filterOptions) {
  const results = [];
  shiftsForSession.forEach((value) => {
    let filteredShifts = value.sessionGrouping.shifts;

    // Find all the shifts that have activities that the client IDs given are on
    if (
      has(filterOptions, "filteredClients") &&
      filterOptions.filteredClients.length > 0
    ) {
      if (!has(filterOptions, "activities")) {
        throw new Error(
          "activities array must be supplied with filteredClients",
        );
      }

      filteredShifts = getShiftsFilteredByClients(
        filteredShifts,
        filterOptions.filteredClients,
        filterOptions.activities,
      );
    }

    // find shifts matching the duties filter
    if (
      has(filterOptions, "filteredDuties") &&
      filterOptions.filteredDuties.length > 0
    ) {
      filteredShifts = filteredShifts.filter((shift) => {
        const duties = get(shift, "dutiesRequired", []);
        return filterOptions.filteredDuties.some((duty) =>
          duties.includes(duty),
        );
      });
    }

    if (filteredShifts.length > 0) {
      const newValue = {
        ...value,
      };
      newValue.sessionGrouping.shifts = filteredShifts;
      results.push(newValue);
    }
  });
  return results;
}

/**
 * Returns a unique array of clients that are on this shift.
 * Used primarily to display client badges on the rota display.
 *
 * @param {Object} shift
 * @param {Array} activities
 * @returns {Array} clients
 */
function getUniqueClientsOnShift(shift, activities) {
  if (!has(shift, "activities")) {
    return [];
  }
  // find activities that are on this shift
  const matchingActivities = getActivitiesForShift(activities, shift);

  // get the clients on those activities
  const clients = flatMap(matchingActivities, "clients");

  // unique the clients
  return uniqBy(clients, (client) => client.clientId);
}

/*
 * @param {Array} shifts
 * @param {Object} session
 * @param {Date} rotaStartDate
 * @param {int} startDayIndex Integer representing the day number since the rotaStartDate
 * @param {int} endDayIndex Integer representing the day number since the rotaStartDate
 * @param {array} allowedAssignmentTypes Array of the types of assignments you want returned
 *                                       (ignores old assignments by default)
 * @returns {Array}
 */
function getShiftsForUserId(
  shifts,
  userId,
  rotaStartDate = undefined,
  startDayIndex = undefined,
  endDayIndex = undefined,
  allowedAssignmentTypes = ["current", "sick", "holiday"],
) {
  if (!Array.isArray(shifts)) {
    return [];
  }

  let filteredShifts = shifts;

  // we might want to filter the shifts to a particular date range
  if (
    rotaStartDate !== undefined &&
    isDate(rotaStartDate) &&
    isInteger(startDayIndex) &&
    isInteger(endDayIndex)
  ) {
    filteredShifts = filterShiftsForDayIndexRange(
      filteredShifts,
      rotaStartDate,
      startDayIndex,
      endDayIndex,
    );
  }

  if (filteredShifts.length === 0) {
    return [];
  }

  const shiftsByUserId = [];

  filteredShifts.forEach((shift) => {
    forEach(shift.assignedTo, (assignment) => {
      if (!allowedAssignmentTypes.includes(assignment.type)) {
        // ignore assignments that are not of the correct type
        return;
      }
      if (has(assignment, "userId") && assignment.userId === userId) {
        if (!shiftsByUserId.includes(shift)) {
          shiftsByUserId.push(shift);
        }
      } else if (
        has(assignment, "agencyId") &&
        assignment.agencyId === userId
      ) {
        if (!shiftsByUserId.includes(shift)) {
          shiftsByUserId.push(shift);
        }
      }
    });
  });
  return shiftsByUserId;
}

/*
 * Like getShiftsForUserId, but also returns shifts sorted by date, assigned to 'nobody'
 * as long as they don't clash with our user's normal shifts. Used for the agenda view
 * to show the 'unassigned' shifts that the user might want to sign up to.
 * As of CC-1364 this excludes shifts that have coverNotRequired set to true
 *
 * @param {Array} shifts
 * @param {Object} session
 * @param {Date} rotaStartDate
 * @param {int} startDayIndex Integer representing the day number since the rotaStartDate
 * @param {int} endDayIndex Integer representing the day number since the rotaStartDate
 * @param {array} allowedAssignmentTypes Array of the types of assignments you want returned
 *                                       (ignores old assignments by default)
 * @returns {Array}
 */
function getShiftsForNobodyThatDontClashWithUserId(
  shifts,
  userId,
  rotaStartDate = undefined,
  startDayIndex = undefined,
  endDayIndex = undefined,
  allowedAssignmentTypes = ["current", "sick", "holiday"],
) {
  if (!Array.isArray(shifts)) {
    return [];
  }

  const userShifts = getShiftsForUserId(
    shifts,
    userId,
    rotaStartDate,
    startDayIndex,
    endDayIndex,
    allowedAssignmentTypes,
  );

  let filteredShifts = shifts;
  if (
    isDate(rotaStartDate) &&
    isInteger(startDayIndex) &&
    isInteger(endDayIndex)
  ) {
    filteredShifts = filterShiftsForDayIndexRange(
      filteredShifts,
      rotaStartDate,
      startDayIndex,
      endDayIndex,
    );
  }

  if (filteredShifts.length === 0) {
    return [];
  }
  const nobodyShifts = [];
  filteredShifts.forEach((shift) => {
    let isAssigned = false;
    if (get(shift, "coverNotRequired", false) === true) {
      return;
    }
    // ignore current assignments
    forEach(shift.assignedTo, (assignment) => {
      if (assignment.type === "current") {
        isAssigned = true;
      }
    });
    if (!isAssigned) {
      // see if it clashes with one of our shifts in userShifts by using areIntervalsOverlapping
      let isOverlapping = false;
      if (isPast(shift.startDate)) {
        // has the shift already started?
        isOverlapping = true;
      } else {
        userShifts.forEach((userShift) => {
          try {
            if (
              isOverlapping ||
              areIntervalsOverlapping(
                { start: shift.startDate, end: shift.endDate },
                { start: userShift.startDate, end: userShift.endDate },
              )
            ) {
              isOverlapping = true;
            }
          } /* v8 ignore next 3 */ catch {
            // do nothing
          }
        });
      }
      if (!isOverlapping) {
        nobodyShifts.push(shift);
      }
    }
  });
  nobodyShifts.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  return nobodyShifts;
}

/**
 * Returns a unique array of users that are currently assignedTo this rota.
 * Filtered by dayIndex, comparisonShift, and optionally excluding the userId you pass it.
 *
 * @param {Object} rota
 * @param {int} dayIndex
 * @param {Object} comparisonShift
 * @param {String} excludeUserId
 * @return {array} [ { userId: '', displayName: '' }, ... ]
 */

function getUniqueUsersOnRotaForDayIndex(
  rota,
  dayIndex,
  comparisonShift = null,
  excludeUserId = null,
) {
  // filter shifts by session + day
  let filteredShifts = filterShiftsForDayIndex(
    rota.shifts,
    rota.startDate,
    dayIndex,
  );
  if (comparisonShift !== null) {
    // compare against the given shift's start/end time to work out if
    // the shift starts within the same time span
    filteredShifts = filteredShifts.filter(
      /* v8 ignore next 5 */
      (shift) =>
        isEqual(shift.startDate, comparisonShift.startDate) ||
        (isAfter(shift.startDate, comparisonShift.startDate) &&
          isBefore(shift.startDate, comparisonShift.endDate)),
    );
  }

  // look for current assignments
  const shiftsByUserId = [];
  filteredShifts.forEach((shift) => {
    let current = find(get(shift, "assignedTo", []), { type: "current" });

    if (
      excludeUserId !== null &&
      current !== undefined &&
      (excludeUserId === current.userId || excludeUserId === current.agencyId)
    ) {
      current = undefined;
    }
    if (current !== undefined) {
      if (current.userId) {
        const data = {
          userId: current.userId,
          displayName: current.displayName,
          dutiesRequired: defaultTo(shift.dutiesRequired, []),
        };
        if (find(shiftsByUserId, { userId: data.userId }) === undefined) {
          shiftsByUserId.push(data);
        }
      }
      if (current.agencyId) {
        const data = {
          agencyId: current.agencyId,
          displayName: current.displayName,
          dutiesRequired: defaultTo(shift.dutiesRequired, []),
        };
        if (find(shiftsByUserId, { agencyId: data.agencyId }) === undefined) {
          shiftsByUserId.push(data);
        }
      }
    }
  });
  // sort by displayName
  shiftsByUserId.sort((a, b) =>
    (a.displayName === undefined ? "" : a.displayName).localeCompare(
      b.displayName === undefined ? "" : b.displayName,
    ),
  );
  return shiftsByUserId;
}

/**
 * Takes a rota and returns the shifts that are linked to this activity
 *
 * @param {Object} rota
 * @param {Object} activity
 * @returns []
 */
function getShiftsFromRotaMatchingActivity(rota, activity) {
  return filter(get(rota, "shifts", []), (shift) =>
    map(get(shift, "activities", []), "activityId").includes(activity.id),
  );
}

/**
 * Converts individual schedule items to include start and end dates based on the start and end times and the shift start and end dates.
 * @param {Object} shift - The shift object containing the start and end dates.
 * @param {Array} schedule - The schedule array containing the start and end times.
 * @returns {Array} An array of schedule items with start and end dates.
 */
function convertScheduleToDates(shift, schedule) {
  if (!shift || !shift.startDate || !shift.endDate || !schedule) {
    return [];
  }

  return schedule
    .filter((item) => item.startTime && item.endTime)
    .map((item) => {
      const startDateTime = new Date(shift.startDate);
      const endDateTime = new Date(shift.startDate);

      const [startHour, startMinute] = item.startTime.split(":");
      const [endHour, endMinute] = item.endTime.split(":");

      startDateTime.setHours(startHour, startMinute, 0, 0);
      endDateTime.setHours(endHour, endMinute, 0, 0);

      if (startDateTime < shift.startDate) {
        startDateTime.setDate(startDateTime.getDate() + 1);
      }
      if (endDateTime < shift.startDate) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }

      return {
        ...item,
        startDate: startDateTime,
        endDate: endDateTime,
      };
    })
    .filter((item) => item !== null);
}

function convertScheduleCareToCareActivities(
  rota,
  allClients,
  shift,
  schedule,
) {
  if (!shift || !shift.startDate || !shift.endDate || !schedule) {
    return false;
  }

  const clientInitialsRegexp = new RegExp(
    "^([a-zA-Z]{2})\\W|\\W([a-zA-Z]{2})$",
  );

  // convert the schedule to include start and end dates
  const activities = convertScheduleToDates(shift, schedule)
    .filter((item) => item.startTime && item.endTime && item.type === "care")
    .map((item) => {
      const activity = {
        type: CARE_TYPE,
        title: CARE_TYPE,
        master: rota.status === "master",
        locationId: rota.location,
        rotaId: rota.id,
        personsRequired: 1,
        startDate: item.startDate,
        endDate: item.endDate,
        // set the shifts property so that we don't need to call a separate 'assign' API endpoint
        shifts: [
          {
            rotaId: rota.id,
            shiftId: shift.id,
          },
        ],
      };
      if (item.notes) {
        activity.note = item.notes.trim();
      }

      // find matching clients
      if (clientInitialsRegexp.test(item.notes)) {
        const match = item.notes.match(clientInitialsRegexp);
        if (match) {
          if (activity.clients === undefined) {
            activity.clients = [];
          }
          // get the initials from the regex match
          const clientInitials = (match[1] || match[2]).toUpperCase();

          // find client
          const clientMatch = allClients.find(
            (c) => c.initials === clientInitials,
          );
          if (clientMatch) {
            activity.clients.push({
              clientId: clientMatch.id,
              initials: clientInitials,
              location: clientMatch.location,
            });
          }
        }
      }

      return activity;
    });

  // return a new schedule with the care activities removed
  const newSchedule = schedule.filter(
    (item) => item.startTime && item.endTime && item.type != "care",
  );

  return {
    activities,
    newSchedule,
  };
}

const getShiftPreviousAssignment = (shift) => {
  return findLast(shift.assignedTo, {
    type: "old",
  });
};

const getShiftCurrentAssignment = (shift) => {
  if (!shift.assignedTo || shift.assignedTo.length === 0) {
    return undefined;
  }
  return findLast(shift.assignedTo, {
    type: "current",
  });
};

const getVolunteeredForShift = (shift, userId) => {
  return findLast(shift.coverRequest, {
    status: SHIFTCOVER_STATUS_VOLUNTEERED,
    userId: userId,
  });
};

const getIsMyShift = (shift, userId) => {
  if (!shift.assignedTo || shift.assignedTo.length === 0) {
    return false;
  }
  const lastEntry = findLast(shift.assignedTo, {
    type: "current",
  });
  if (lastEntry && lastEntry.userId === userId) {
    return true;
  }
  return false;
};

const getWasMyShift = (shift, userId) => {
  return (shift.assignedTo ?? []).some(
    (assignment) => assignment.userId === userId,
  );
};

const getShiftNotCurrentlyAssignedToAnyone = (shift) => {
  if (shift.assignedTo === undefined || shift.assignedTo.length === 0) {
    return true;
  }
  if (
    shift.assignedTo.length > 0 &&
    shift.assignedTo[shift.assignedTo.length - 1].type !== "current"
  ) {
    return true;
  }
  return false;
};

const canSeeShiftCover = (shift, userId) => {
  if (getIsMyShift(shift, userId)) {
    return true;
  }
  if (getWasMyShift(shift, userId)) {
    return true;
  }
  if (getShiftNotCurrentlyAssignedToAnyone(shift)) {
    return true;
  }
  if (getVolunteeredForShift(shift, userId)) {
    return true;
  }
  if (shift.coverRequestStatus === SHIFTCOVER_STATUS_OPEN) {
    return true;
  }
  if (shift.coverRequestStatus === SHIFTCOVER_STATUS_VOLUNTEERED) {
    return true;
  }
  return false;
};

const getShiftCoverProblems = (shift, rotaUser, rotasToCheck) => {
  const problems = [];
  // check to see if the shift dutiesRequired match the rotaUser.dutiesQualifiedFor
  if (shift.dutiesRequired && shift.dutiesRequired.length > 0) {
    const missing = shift.dutiesRequired.filter(
      (duty) => !rotaUser.dutiesQualifiedFor?.includes(duty),
    );
    if (missing.length > 0) {
      problems.push({
        problem: `Missing duties: ${missing.join(", ")}`,
      });
    }
  }

  // check to see if there are any clashes with the shift
  // get all shifts
  const userShifts = rotasToCheck.flatMap((r) => {
    return filterCurrentShiftsForUser(r.shifts, rotaUser.userId).map((s) => ({
      ...s,
      rotaId: r.id,
    }));
  });

  // check for clashes based on start and end time
  const userShiftClashes = userShifts
    .filter((s) => {
      return s.startDate < shift.endDate && s.endDate > shift.startDate;
    })
    .map((s) => {
      return {
        ...s,
        problem: "Clashing shift",
      };
    });

  const userShiftAdjacent = userShifts
    .filter((s) => {
      const diff1 =
        parseInt((s.startDate.getTime() / 1000).toFixed(0)) -
        parseInt((shift.endDate.getTime() / 1000).toFixed(0));
      const diff2 =
        parseInt((s.endDate.getTime() / 1000).toFixed(0)) -
        parseInt((shift.startDate.getTime() / 1000).toFixed(0));
      return (
        Math.abs(diff1) <= MIN_SHIFT_COVER_GAP_SECONDS ||
        Math.abs(diff2) <= MIN_SHIFT_COVER_GAP_SECONDS
      );
    })
    .map((s) => {
      return {
        ...s,
        problem: "Adjacent shift (within 1 hour)",
      };
    });

  return [
    ...problems,
    ...uniqBy([...userShiftClashes, ...userShiftAdjacent], "id"),
  ];
};

export {
  getShiftsForSessionGroupedByAssignedTo,
  getShiftsForSessionGroupedByAssignedToSorted,
  filterGroupedShifts,
  getShiftsFilteredByClients,
  filterShiftsForUser,
  filterCurrentShiftsForUser,
  filterShiftsNotCurrentlyAssigned,
  filterShiftsWithCoverRequestsOrNotCurrentlyAssigned,
  filterCurrentShiftsForAgencyId,
  filterShiftsForDayIndex,
  filterShiftsForDayIndexRange,
  getDayIndexForShiftRelativeToRota,
  getShiftCountsForSessionAndDayIndex,
  shiftStartsInSession,
  shiftHasCustomTimes,
  getShiftSession,
  getMostRecentAssignmentFromUserId,
  shiftIsAssignedToAgency,
  sortShiftGroupings,
  sortShiftGroupingsByShiftTime,
  getActivitiesForShift,
  getActivitiesWithinShiftTimes,
  getActivitiesWithinShiftDay,
  getUniqueClientsOnShift,
  getShiftsForUserId,
  getShiftsForNobodyThatDontClashWithUserId,
  getUniqueUsersOnRotaForDayIndex,
  getShiftsFromRotaMatchingActivity,
  convertScheduleToDates,
  convertScheduleCareToCareActivities,
  // shift cover stuff
  getShiftCurrentAssignment,
  getShiftPreviousAssignment,
  getIsMyShift,
  getWasMyShift,
  getVolunteeredForShift,
  getShiftNotCurrentlyAssignedToAnyone,
  canSeeShiftCover,
  getShiftCoverProblems,
};
